import { useState } from 'react';
import { api, errorMessage } from '../../shared/api/client';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { formatMoney, parseMoney } from '../finance/money';
import { useMe } from '../session/SessionProvider';
import { personOptions, SearchSelect } from '../../shared/ui/SearchSelect';

type Ref = { personId: string; name: string };
const KINDS: [string, string][] = [['alimentos', 'Alimentos'], ['ropa', 'Ropa'], ['transporte', 'Transporte'], ['medicinas', 'Medicinas'], ['asistencia', 'Asistencia'], ['otro', 'Otro']];
const STATUS: Record<string, string> = { abierta: 'Abierta', en_proceso: 'En proceso', atendida: 'Atendida', cerrada: 'Cerrada' };
const kindLabel = (k: string) => KINDS.find(([x]) => x === k)?.[1] ?? k;
const day = (iso: string) => new Date(iso).toLocaleDateString('es', { day: 'numeric', month: 'short' });

function useRun(reload: () => void) {
  const [msg, setMsg] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const run = async (action: () => Promise<unknown>, ok: string | null) => {
    setBusy(true);
    setMsg(null);
    try {
      await action();
      if (ok) setMsg({ tone: 'success', text: ok });
      reload();
      return true;
    } catch (e) {
      setMsg({ tone: 'error', text: errorMessage(e) });
      return false;
    } finally {
      setBusy(false);
    }
  };
  return { msg, busy, run };
}

type Help = {
  canHelp: boolean; team: Ref[];
  requests: { id: string; kind: string; forWhom: string | null; contact: string | null; details: string | null; status: string; helper: Ref | null; notes: string | null; createdAt: string; mine: boolean }[];
  donations: { id: string; requestId: string | null; donor: string | null; item: string; quantity: string | null; received: boolean; createdAt: string }[];
};

/** Ayuda comunitaria: pedir ayuda (privado), ofrecer donativos y, para Servicio Social, atender. */
export function HelpPage() {
  const me = useMe();
  const base = `/organization/churches/${me.activeChurchId}`;
  const data = useAsync((signal) => (me.activeChurchId ? api.get<Help>(`${base}/help`, signal) : Promise.resolve(null)), [base]);
  const { msg, busy, run } = useRun(() => data.reload());
  const [forSelf, setForSelf] = useState(true);
  // Dos botones grandes; cada uno abre solo su formulario.
  const [showing, setShowing] = useState<'pedir' | 'donar' | null>(null);
  if (!me.activeChurchId) return <EmptyState title="Elige primero una iglesia." />;
  if (data.status === 'loading') return <div className="page"><Loading /></div>;
  if (data.status === 'error') return <div className="page"><ErrorState error={data.error} onRetry={data.reload} /></div>;
  const d = data.data;
  if (!d) return null;
  return (
    <div className="page page--narrow">
      <header className="page__header">
        <h1>Ayuda comunitaria</h1>
        <p className="muted">¿Necesitas algo o sabes de alguien que lo necesita? Lo atiende Servicio Social con discreción.</p>
      </header>
      {msg ? <Alert tone={msg.tone}>{msg.text}</Alert> : null}
      <div className="actions actions--wrap">
        <Button variant={showing === 'pedir' ? 'primary' : 'secondary'} aria-expanded={showing === 'pedir'} onClick={() => setShowing(showing === 'pedir' ? null : 'pedir')}>
          Necesito ayuda
        </Button>
        <Button variant={showing === 'donar' ? 'primary' : 'secondary'} aria-expanded={showing === 'donar'} onClick={() => setShowing(showing === 'donar' ? null : 'donar')}>
          Quiero donar
        </Button>
      </div>
      {showing === 'pedir' ? (
      <Card title="Necesito ayuda">
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            const form = e.currentTarget;
            const f = new FormData(form);
            const t = (k: string) => String(f.get(k) ?? '').trim() || null;
            void run(() => api.post(`${base}/help`, { kind: t('kind'), forSelf, forName: forSelf ? null : t('forName'), contact: t('contact'), details: t('details') }), 'Recibido. Servicio Social te contactará.').then((ok) => ok && setShowing(null));
          }}
        >
          <div className="field"><label htmlFor="h-kind">¿Qué se necesita?</label><select id="h-kind" name="kind">{KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
          <fieldset className="choices choices--row">
            <legend>¿Para quién?</legend>
            <label className="choice"><input type="radio" checked={forSelf} onChange={() => setForSelf(true)} /> <span>Para mí</span></label>
            <label className="choice"><input type="radio" checked={!forSelf} onChange={() => setForSelf(false)} /> <span>Para otra persona</span></label>
          </fieldset>
          {!forSelf ? <Field label="Nombre" name="forName" required maxLength={120} /> : null}
          <Field label="Teléfono o cómo contactar" name="contact" maxLength={120} />
          <div className="field"><label htmlFor="h-det">Detalles</label><textarea id="h-det" name="details" rows={3} maxLength={2000} /></div>
          <p className="small muted">Solo lo ven Servicio Social y Pastoral.</p>
          <div className="actions"><Button type="submit" busy={busy}>Enviar</Button><Button type="button" variant="ghost" onClick={() => setShowing(null)}>Cancelar</Button></div>
        </form>
      </Card>
      ) : null}
      {showing === 'donar' ? (
      <Card title="Quiero donar">
        <form className="family-add" onSubmit={(e) => { e.preventDefault(); const f = new FormData(e.currentTarget); void run(() => api.post(`${base}/help-donations`, { item: String(f.get('item') ?? '').trim(), quantity: String(f.get('qty') ?? '').trim() || null }), '¡Gracias! Servicio Social te dirá dónde entregarlo.').then((ok) => ok && setShowing(null)); }}>
          <Field label="¿Qué donas?" name="item" required maxLength={200} placeholder="Despensa, cobijas, ropa de niño…" />
          <Field label="Cantidad" name="qty" maxLength={60} />
          <Button type="submit" busy={busy}>Ofrecer</Button>
          <Button type="button" variant="ghost" onClick={() => setShowing(null)}>Cancelar</Button>
        </form>
      </Card>
      ) : null}
      <h2 className="small muted">{d.canHelp ? 'Necesidades' : 'Mis solicitudes'}</h2>
      {d.requests.length === 0 ? <Card><EmptyState title="Nada por ahora." /></Card> : null}
      {d.requests.map((r) => (
        <Card key={r.id} title={`${kindLabel(r.kind)} · ${r.forWhom ?? ''}`} action={<span className={`badge ${r.status === 'abierta' ? 'badge--warn' : 'badge--active'}`}>{STATUS[r.status]}</span>}>
          {r.details ? <p className="pre-line">{r.details}</p> : null}
          <p className="small muted">{day(r.createdAt)}{r.contact ? ` · ${r.contact}` : ''}{r.helper ? ` · la atiende ${r.helper.name}` : ''}</p>
          {d.canHelp ? (
            <form className="form" onSubmit={(e) => { e.preventDefault(); const f = new FormData(e.currentTarget); void run(() => api.put(`${base}/help/${r.id}`, { status: String(f.get('status')), helperId: String(f.get('helper') ?? '') || null, notes: String(f.get('notes') ?? '').trim() || null }), 'Actualizado.'); }}>
              <div className="form__row">
                <div className="field"><label htmlFor={`hs-${r.id}`}>Estado</label><select id={`hs-${r.id}`} name="status" defaultValue={r.status}>{Object.entries(STATUS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
                <div className="field"><label htmlFor={`hh-${r.id}`}>La atiende</label><SearchSelect id={`hh-${r.id}`} name="helper" defaultValue={r.helper?.personId ?? ''} emptyLabel="Nadie aún" options={personOptions(d.team)} /></div>
              </div>
              <div className="field"><label htmlFor={`hn-${r.id}`}>Notas (privadas)</label><textarea id={`hn-${r.id}`} name="notes" rows={2} maxLength={2000} defaultValue={r.notes ?? ''} /></div>
              <Button type="submit" variant="secondary" busy={busy}>Guardar</Button>
            </form>
          ) : null}
        </Card>
      ))}
      {d.canHelp && d.donations.length ? (
        <Card title="Donativos ofrecidos">
          <ul className="list">
            {d.donations.map((x) => (
              <li key={x.id} className="list__item">
                <span>{x.item}{x.quantity ? ` × ${x.quantity}` : ''} · {x.donor ?? 'Anónimo'} · {day(x.createdAt)}</span>
                {x.received ? <span className="badge badge--active">Recibido</span> : <Button variant="secondary" busy={busy} onClick={() => void run(() => api.post(`${base}/help-donations/${x.id}/received`), 'Marcado como recibido.')}>Recibido</Button>}
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
    </div>
  );
}

type Mission = {
  id: string; kind: string; name: string; place: string | null; description: string | null; startsOn: string | null; endsOn: string | null; goalCents: number | null; needs: string | null;
  team: (Ref & { role: string | null })[]; canPost: boolean; updates: { id: string; author: Ref; body: string; createdAt: string }[];
};
const MKIND: Record<string, string> = { viaje: 'Viaje misionero', proyecto: 'Proyecto', misionero: 'Misionero' };

/** Lee el formulario de una misión (crear o editar). */
function readMission(f: FormData) {
  const t = (k: string) => String(f.get(k) ?? '').trim() || null;
  return { kind: t('kind'), name: t('name'), place: t('place'), description: t('description'), startsOn: t('startsOn'), endsOn: t('endsOn'), goalCents: t('goal') ? parseMoney(t('goal')!) : null, needs: t('needs') };
}

function MissionFields({ m, id }: { m?: Mission; id: string }) {
  return (
    <>
      <div className="form__row">
        <div className="field"><label htmlFor={`${id}-kind`}>Tipo</label><select id={`${id}-kind`} name="kind" defaultValue={m?.kind}>{Object.entries(MKIND).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
        <Field label="Nombre" name="name" required minLength={3} maxLength={120} defaultValue={m?.name} />
      </div>
      <Field label="Lugar" name="place" maxLength={150} defaultValue={m?.place ?? ''} />
      <div className="form__row"><Field label="Desde" name="startsOn" type="date" defaultValue={m?.startsOn ?? ''} /><Field label="Hasta" name="endsOn" type="date" defaultValue={m?.endsOn ?? ''} /></div>
      <Field label="Meta de ofrenda (opcional)" name="goal" inputMode="decimal" defaultValue={m?.goalCents ? String(m.goalCents / 100) : ''} />
      <div className="field"><label htmlFor={`${id}-desc`}>Descripción</label><textarea id={`${id}-desc`} name="description" rows={3} maxLength={3000} defaultValue={m?.description ?? ''} /></div>
      <div className="field"><label htmlFor={`${id}-needs`}>Necesidades</label><textarea id={`${id}-needs`} name="needs" rows={2} maxLength={2000} defaultValue={m?.needs ?? ''} /></div>
    </>
  );
}

/** Misiones: proyectos, viajes y misioneros; equipo, necesidades e informes. */
export function MissionsPage() {
  const me = useMe();
  const base = `/organization/churches/${me.activeChurchId}/missions`;
  const data = useAsync((signal) => (me.activeChurchId ? api.get<{ canManage: boolean; items: Mission[] }>(base, signal) : Promise.resolve(null)), [base]);
  const people = useAsync((signal) => (me.activeChurchId && me.can('members.read') ? api.get<{ items: { personId: string; givenName: string; familyName: string }[] }>(`/multichurch/churches/${me.activeChurchId}/members`, signal).then((r) => r.items).catch(() => []) : Promise.resolve([])), [me.activeChurchId]);
  const { msg, busy, run } = useRun(() => data.reload());
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  if (!me.activeChurchId) return <EmptyState title="Elige primero una iglesia." />;
  if (data.status === 'loading') return <div className="page"><Loading /></div>;
  if (data.status === 'error') return <div className="page"><ErrorState error={data.error} onRetry={data.reload} /></div>;
  const d = data.data;
  if (!d) return null;
  return (
    <div className="page page--narrow">
      <header className="page__header">
        <h1>Misiones</h1>
        <p className="muted">Viajes, proyectos y misioneros que la iglesia apoya.</p>
      </header>
      {msg ? <Alert tone={msg.tone}>{msg.text}</Alert> : null}
      {d.canManage && !open ? <div><Button onClick={() => setOpen(true)}>Nueva misión</Button></div> : null}
      {open ? (
        <Card title="Nueva misión">
          <form className="form" onSubmit={(e) => {
            e.preventDefault();
            void run(() => api.post(base, readMission(new FormData(e.currentTarget))), 'Misión creada.').then((ok) => ok && setOpen(false));
          }}>
            <MissionFields id="m" />
            <div className="actions"><Button type="submit" busy={busy}>Crear</Button><Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button></div>
          </form>
        </Card>
      ) : null}
      {d.items.length === 0 ? <Card><EmptyState title="Todavía no hay misiones." /></Card> : null}
      {d.items.map((m) => (
        <Card key={m.id} title={m.name} action={<span className="actions"><span className="small muted">{MKIND[m.kind]}</span>{d.canManage && editing !== m.id ? <Button variant="ghost" aria-label={`Editar ${m.name}`} onClick={() => setEditing(m.id)}>Editar</Button> : null}</span>}>
          {editing === m.id ? (
            <form className="form" onSubmit={(e) => { e.preventDefault(); void run(() => api.put(`${base}/${m.id}`, readMission(new FormData(e.currentTarget))), 'Misión actualizada.').then((ok) => ok && setEditing(null)); }}>
              <MissionFields m={m} id={`me-${m.id}`} />
              <div className="actions">
                <Button type="submit" busy={busy}>Guardar</Button>
                <Button type="button" variant="ghost" onClick={() => setEditing(null)}>Cancelar</Button>
                <Button type="button" variant="danger" busy={busy} onClick={() => { if (window.confirm(`¿Borrar la misión ${m.name}? Se borran su equipo e informes.`)) void run(() => api.delete(`${base}/${m.id}`), 'Misión borrada.').then((ok) => ok && setEditing(null)); }}>Borrar misión</Button>
              </div>
            </form>
          ) : null}
          <p className="small muted">{[m.place, m.startsOn ? `${day(m.startsOn)}${m.endsOn ? ` – ${day(m.endsOn)}` : ''}` : null, m.goalCents ? `meta ${formatMoney(m.goalCents, 'MXN')}` : null].filter(Boolean).join(' · ')}</p>
          {m.description ? <p className="pre-line">{m.description}</p> : null}
          {m.needs ? <p className="pre-line"><strong>Necesidades:</strong> {m.needs}</p> : null}
          {d.canManage && m.team.length ? (
            <>
              <h3 className="small">Equipo</h3>
              <ul className="list" aria-label={`Equipo de ${m.name}`}>
                {m.team.map((x) => (
                  <li key={x.personId} className="list__item">
                    <span>{x.name}{x.role ? ` (${x.role})` : ''}</span>
                    <Button variant="ghost" busy={busy} aria-label={`Quitar a ${x.name} del equipo`} onClick={() => { if (window.confirm(`¿Quitar a ${x.name} del equipo de ${m.name}?`)) void run(() => api.put(`${base}/${m.id}/team/${x.personId}`, { on: false }), 'Quitado del equipo.'); }}>Quitar</Button>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="small"><strong>Equipo:</strong> {m.team.length ? m.team.map((x) => `${x.name}${x.role ? ` (${x.role})` : ''}`).join(', ') : 'por formar'}</p>
          )}
          {d.canManage && people.status === 'ready' && people.data.length ? (
            <form className="family-add" onSubmit={(e) => { e.preventDefault(); const f = new FormData(e.currentTarget); const pid = String(f.get('person') ?? ''); if (pid) void run(() => api.put(`${base}/${m.id}/team/${pid}`, { role: String(f.get('role') ?? '').trim() || null, on: true }), 'Agregado al equipo.'); }}>
              <div className="field"><label htmlFor={`mt-${m.id}`}>Agregar al equipo</label><SearchSelect id={`mt-${m.id}`} name="person" options={people.data.map((p) => ({ value: p.personId, label: `${p.givenName} ${p.familyName}`.trim() }))} /></div>
              <Field label="Rol" name="role" maxLength={60} />
              <Button type="submit" variant="secondary" busy={busy}>Agregar</Button>
            </form>
          ) : null}
          {m.updates.length ? (
            <>
              <h3 className="small">Informes</h3>
              <ul className="list">{m.updates.map((u) => <li key={u.id} className="list__item list__item--stack"><span className="small muted">{u.author.name} · {day(u.createdAt)}</span><span className="pre-line">{u.body}</span></li>)}</ul>
            </>
          ) : null}
          {m.canPost ? (
            <form className="family-add" onSubmit={(e) => { e.preventDefault(); const form = e.currentTarget; const body = String(new FormData(form).get('body') ?? '').trim(); if (body) void run(() => api.post(`${base}/${m.id}/updates`, { body }), 'Informe publicado.').then((ok) => ok && form.reset()); }}>
              <Field label="Nuevo informe" name="body" maxLength={4000} />
              <Button type="submit" variant="secondary" busy={busy}>Publicar</Button>
            </form>
          ) : null}
        </Card>
      ))}
    </div>
  );
}
